"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { type DayButton, DayPicker, getDefaultClassNames, type Locale } from "react-day-picker";
import { Button, buttonVariants } from "~/components/ui/button";
import { cn } from "~/lib/utils";
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  locale,
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
}) {
  const defaultClassNames = getDefaultClassNames();
  return <DayPicker showOutsideDays={showOutsideDays} className={cn("group/calendar bg-background p-3 [--cell-radius:var(--radius-4xl)] [--cell-size:--spacing(8)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent", String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`, String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`, className)} captionLayout={captionLayout} locale={locale} formatters={{
    formatMonthDropdown: date => date.toLocaleString(locale?.code, {
      month: "short"
    }),
    ...formatters
  }} classNames={{
    root: cn("w-fit", defaultClassNames.root),
    months: cn("relative flex flex-col gap-4 md:flex-row", defaultClassNames.months),
    month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
    nav: cn("absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1", defaultClassNames.nav),
    button_previous: cn(buttonVariants({
      variant: buttonVariant
    }), "size-(--cell-size) p-0 select-none aria-disabled:opacity-50", defaultClassNames.button_previous),
    button_next: cn(buttonVariants({
      variant: buttonVariant
    }), "size-(--cell-size) p-0 select-none aria-disabled:opacity-50", defaultClassNames.button_next),
    month_caption: cn("flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)", defaultClassNames.month_caption),
    dropdowns: cn("flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium", defaultClassNames.dropdowns),
    dropdown_root: cn("relative rounded-(--cell-radius)", defaultClassNames.dropdown_root),
    dropdown: cn("absolute inset-0 bg-popover opacity-0", defaultClassNames.dropdown),
    caption_label: cn("font-medium select-none", captionLayout === "label" ? "text-sm" : "flex items-center gap-1 rounded-(--cell-radius) text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground", defaultClassNames.caption_label),
    table: "w-full border-collapse",
    weekdays: cn("flex", defaultClassNames.weekdays),
    weekday: cn("flex-1 rounded-(--cell-radius) text-[0.8rem] font-normal text-muted-foreground select-none", defaultClassNames.weekday),
    week: cn("mt-2 flex w-full", defaultClassNames.week),
    week_number_header: cn("w-(--cell-size) select-none", defaultClassNames.week_number_header),
    week_number: cn("text-[0.8rem] text-muted-foreground select-none", defaultClassNames.week_number),
    day: cn("group/day relative aspect-square h-full w-full rounded-(--cell-radius) p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius)", props.showWeekNumber ? "[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)" : "[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)", defaultClassNames.day),
    range_start: cn("relative isolate z-0 rounded-l-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:right-0 after:w-4 after:bg-muted", defaultClassNames.range_start),
    range_middle: cn("rounded-none", defaultClassNames.range_middle),
    range_end: cn("relative isolate z-0 rounded-r-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:left-0 after:w-4 after:bg-muted", defaultClassNames.range_end),
    today: cn("rounded-(--cell-radius) bg-muted text-foreground data-[selected=true]:rounded-none", defaultClassNames.today),
    outside: cn("text-muted-foreground aria-selected:text-muted-foreground", defaultClassNames.outside),
    disabled: cn("text-muted-foreground opacity-50", defaultClassNames.disabled),
    hidden: cn("invisible", defaultClassNames.hidden),
    ...classNames
  }} components={{
    Root: ({
      className: className_0,
      rootRef,
      ...props_0
    }) => {
      return <div data-slot="calendar" ref={rootRef} className={cn(className_0)} {...props_0} />;
    },
    Chevron: ({
      className: className_1,
      orientation,
      ...props_1
    }) => {
      if (orientation === "left") {
        return <ChevronLeft strokeWidth={2} className={cn("size-4", className_1)} {...props_1} />;
      }
      if (orientation === "right") {
        return <ChevronRight strokeWidth={2} className={cn("size-4", className_1)} {...props_1} />;
      }
      return <ChevronDown strokeWidth={2} className={cn("size-4", className_1)} {...props_1} />;
    },
    DayButton: ({
      ...props_2
    }) => <CalendarDayButton locale={locale} {...props_2} />,
    WeekNumber: ({
      children,
      ...props_3
    }) => {
      return <td {...props_3}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>;
    },
    ...components
  }} {...props} />;
}
function CalendarDayButton(t0) {
  const $ = _c(21);
  let className;
  let day;
  let locale;
  let modifiers;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      day,
      modifiers,
      locale,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = day;
    $[3] = locale;
    $[4] = modifiers;
    $[5] = props;
  } else {
    className = $[1];
    day = $[2];
    locale = $[3];
    modifiers = $[4];
    props = $[5];
  }
  const defaultClassNames = getDefaultClassNames();
  const ref = React.useRef(null);
  let t1;
  let t2;
  if ($[6] !== modifiers.focused) {
    t1 = () => {
      if (modifiers.focused) {
        ref.current?.focus();
      }
    };
    t2 = [modifiers.focused];
    $[6] = modifiers.focused;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  React.useEffect(t1, t2);
  const T0 = Button;
  const t3 = "ghost";
  const t4 = "icon";
  let t5;
  if ($[9] !== day.date || $[10] !== locale?.code) {
    t5 = day.date.toLocaleDateString(locale?.code);
    $[9] = day.date;
    $[10] = locale?.code;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle;
  const t7 = modifiers.range_start;
  const t8 = modifiers.range_end;
  const t9 = modifiers.range_middle;
  const t10 = cn("relative isolate z-10 flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 border-0 leading-none font-normal data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:rounded-r-(--cell-radius) data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-muted data-[range-middle=true]:text-foreground data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:rounded-l-(--cell-radius) data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground dark:hover:text-foreground [&>span]:text-xs [&>span]:opacity-70", defaultClassNames.day, className);
  let t11;
  if ($[12] !== T0 || $[13] !== modifiers.range_end || $[14] !== modifiers.range_middle || $[15] !== modifiers.range_start || $[16] !== props || $[17] !== t10 || $[18] !== t5 || $[19] !== t6) {
    t11 = <T0 ref={ref} variant={t3} size={t4} data-day={t5} data-selected-single={t6} data-range-start={t7} data-range-end={t8} data-range-middle={t9} className={t10} {...props} />;
    $[12] = T0;
    $[13] = modifiers.range_end;
    $[14] = modifiers.range_middle;
    $[15] = modifiers.range_start;
    $[16] = props;
    $[17] = t10;
    $[18] = t5;
    $[19] = t6;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
export { Calendar, CalendarDayButton };